// Fetchers for the admin homepage KPI cards. The order-stage cards get
// { orders, items } in one call — items (Σ order_items.quantity) is the
// headline number, orders the sub-line — from /orders/count, which also
// accepts a comma-separated status list ("picking,picked").

import { apiFetch } from "./apiFetch";

/** Order + item-unit counts for one slice of the orders table. */
export interface OrderStageCounts {
    orders: number;
    items: number;
    // Only on the 'unfulfilled' slice: the part of it that fails the
    // wave-candidacy stock gate (the orders page's Stock blocked pill).
    // Absent when the BE's side-check failed — the card then just omits
    // its stock-blocked line rather than showing a wrong 0.
    stockBlocked?: { orders: number; items: number };
}

const todayYmd = (): string => {
    const d = new Date();
    return d.toISOString().slice(0, 10);
};

// GET /orders/count — lightweight COUNT(*) + SUM(quantity) on the same
// filter set as /orders. Used instead of /orders + array.length so the
// cards stay accurate above 500 (listOrders has a hard LIMIT 500) and
// avoid the per-order item-hydration round-trip.
const fetchOrderCounts = async (params: URLSearchParams): Promise<OrderStageCounts> => {
    const res = await apiFetch(`/orders/count?${params}`);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();
    const out: OrderStageCounts = {
        orders: typeof data?.count === "number" ? data.count : 0,
        items: typeof data?.items === "number" ? data.items : 0,
    };
    const sb = data?.stock_blocked;
    if (sb && typeof sb.count === "number" && typeof sb.items === "number") {
        out.stockBlocked = { orders: sb.count, items: sb.items };
    }
    return out;
};

// Live counts for a pipeline stage — no date range, these are "right
// now" numbers. `statuses` may be a comma-separated list, which the BE
// expands to status = ANY(...).
const fetchStatusCounts = async (statuses: string, warehouse: string): Promise<OrderStageCounts> => {
    const params = new URLSearchParams({ status: statuses });
    if (warehouse) params.set("warehouse", warehouse);
    return fetchOrderCounts(params);
};

// Total unfulfilled workload sitting in the queue at the warehouse.
// True backlog — no date range, since "unfulfilled" is itself the
// operational signal: anything older than today is just deeper queue.
export const fetchUnfulfilledCount = (warehouse: string): Promise<OrderStageCounts> =>
    fetchStatusCounts("unfulfilled", warehouse);

// "In picking" is the whole picking stage: being picked right now plus
// picked-but-not-yet-sorting. The orders page shows these as two
// separate pills, so the card deep-links to the picking pill.
export const fetchInPickingCount = (warehouse: string): Promise<OrderStageCounts> =>
    fetchStatusCounts("picking,picked", warehouse);

// Pick & Sort sorting: orders.status = 'sorting'. A Sorter 1 / Sorter 2
// warehouse never writes that status — see fetchSorterWorkload.
export const fetchInSortingCount = (warehouse: string): Promise<OrderStageCounts> =>
    fetchStatusCounts("sorting", warehouse);

/** How a warehouse fulfils: Pick & Sort waves (orders.status drives the
 *  pipeline cards) or Sorter 1 / Sorter 2 zone storage (Newport, Poland),
 *  where the sorting work lives in the zone-storage collections and the
 *  Portal order jumps from 'unfulfilled' straight to 'dispatch'. */
export type FulfilmentMode = "pick_and_sort" | "sorters";

/** The Items in Sorting card's value, whichever mode produced it. */
export interface SortingCounts extends OrderStageCounts {
    mode: FulfilmentMode;
    // Sorter warehouses only: the per-department split behind the total.
    sorters?: { sorter1: OrderStageCounts; sorter2: OrderStageCounts };
}

// GET /zonestorage/settings/workload — Sorter 1 + Sorter 2 work for a
// sorter warehouse. Resolves null for a Pick & Sort warehouse (the BE
// answers { sorters: false }), which is how the hook learns the mode.
export const fetchSorterWorkload = async (warehouse: string): Promise<SortingCounts | null> => {
    const params = new URLSearchParams({ warehouse });
    const res = await apiFetch(`/zonestorage/settings/workload?${params}`);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();
    if (data?.sorters !== true) return null;
    const stage = (s: unknown): OrderStageCounts => {
        const o = (s ?? {}) as { items?: unknown; orders?: unknown };
        return {
            items: typeof o.items === "number" ? o.items : 0,
            orders: typeof o.orders === "number" ? o.orders : 0,
        };
    };
    return {
        ...stage(data),
        mode: "sorters",
        sorters: { sorter1: stage(data.sorter_1), sorter2: stage(data.sorter_2) },
    };
};

// Sorted and waiting to be packed/shipped. Deliberately counts the
// orders table with status=dispatch — not the sidebar dispatch badge,
// which counts ready units/totes from the dispatch controller.
export const fetchAwaitingDispatchCount = (warehouse: string): Promise<OrderStageCounts> =>
    fetchStatusCounts("dispatch", warehouse);

// Fulfilled today at this warehouse. The BE's eventDate CASE buckets
// fulfilled rows by packed_at, so "today" matches what the dispatch
// search page shows when filtered to today.
export const fetchFulfilledToday = (warehouse: string): Promise<OrderStageCounts> => {
    const today = todayYmd();
    const params = new URLSearchParams({ status: "fulfilled", from: today, to: today });
    if (warehouse) params.set("warehouse", warehouse);
    return fetchOrderCounts(params);
};

// Users with lastActiveAt >= start of today at this warehouse. Bumped
// from the login flow, so "active" specifically means "logged in
// today" — anyone who logged in this morning counts even if they
// closed the tab. Operators who haven't logged in yet today don't.
export const fetchActiveToday = async (warehouse: string): Promise<number> => {
    const params = new URLSearchParams();
    if (warehouse) params.set("warehouse", warehouse);
    const res = await apiFetch(`/login/users/active-today?${params}`);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();
    return typeof data?.count === "number" ? data.count : 0;
};

// Closed totes waiting to be put away at this warehouse. Existing
// /put-away/queue endpoint already returns { count, totes: [...] };
// we only need the count for the KPI.
export const fetchTotesToPutAway = async (warehouse: string): Promise<number> => {
    if (!warehouse) return 0;
    const params = new URLSearchParams({ warehouse });
    const res = await apiFetch(`/put-away/queue?${params}`);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();
    return typeof data?.count === "number" ? data.count : 0;
};
